import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TuneIcon from "@mui/icons-material/Tune";
import axios from "axios";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { setHighlightSegment } from "features/routeReplay/routeReplaySlice";

let globalTripData = null;
let globalTripTitle = "";

const SpeedAnalysis = ({ thresholds, setThresholds, onDrawerStateChange, isInsideSidebar, fetchSpeedThresholds }) => {
  const [refresh, setRefresh] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [speedModalOpen, setSpeedModalOpen] = useState(false);
  const [draftThresholds, setDraftThresholds] = useState(thresholds);
  const dispatch = useDispatch();

  const vehicleId = useSelector((state) => state.vehicle.isVehicle);
  const storedTimes = useSelector((state) => state.vehicle.selectedStartTime);
  const endTimeStored = useSelector((state) => state.vehicle.selectedEndTime);
  const routeReplay = useSelector((state) => state.routeReplay);
  const accountData = useSelector((state) => state.account?.data?.accountModel?.account);

  const devicesObj = routeReplay?.devices?.devices || {};
  const devices = Object.values(devicesObj);
  const deviceLength = Object.keys(devicesObj).length;

  useEffect(() => {
    if (deviceLength === 0) {
      onDrawerStateChange(false);
    }
  }, [deviceLength, onDrawerStateChange]);

  // Make sure Draft updates jab Get API data laaye
  useEffect(() => {
    setDraftThresholds(thresholds);
  }, [thresholds]);

  const handleDraftChange = (key, value) => {
    setDraftThresholds((prev) => ({ ...prev, [key]: Number(value) }));
  };

  const handleOpenSpeedModal = () => {
    setDraftThresholds(thresholds);
    setSpeedModalOpen(true);
  };

  const handleSaveSpeedModal = async () => {
    const token = localStorage.getItem("token");
    const currentOrgId = 
      accountData?.orgHandle || 
      accountData?.orgId || 
      localStorage.getItem("orgtype");

    // UI KO TURANT UPDATE KARO Graph Update Ke Liye
    setThresholds(draftThresholds);
    setSpeedModalOpen(false);

    if (!token || !currentOrgId) return;

    try {
      const payload = {
        speedThresholds: [0, draftThresholds.low, draftThresholds.med, draftThresholds.high]
      };

      await axios.put(`https://v3.dhundhoo.com/org/${currentOrgId}/speedThresholds`, payload, {
        headers: {
          "x-dhundhoo-session": token,
          "orgHandle": currentOrgId,
          "Content-Type": "application/json"
        }
      });
      
      // PUT Success hone ke thodi der baad GET se sync kar lo
      if (fetchSpeedThresholds) {
        setTimeout(() => {
          fetchSpeedThresholds();
        }, 500); 
      }
    } catch (err) {
      console.error("PUT API failed:", err);
    }
  };

  const handleCancelSpeedModal = () => {
    setDraftThresholds(thresholds);
    setSpeedModalOpen(false);
  };

  const labels = {
    low: `< ${thresholds.low}`,
    mid: `${thresholds.low} - ${thresholds.med}`,
    high: `${thresholds.med} - ${thresholds.high}`,
    extreme: `> ${thresholds.high}`,
  };

  const speedCounts = { low: 0, mid: 0, high: 0, extreme: 0 };
  devices.forEach((d) => {
    let speed = (d?.speed ?? 0) * 1.852;
    if (speed < thresholds.low) speedCounts.low++;
    else if (speed >= thresholds.low && speed < thresholds.med) speedCounts.mid++;
    else if (speed >= thresholds.med && speed < thresholds.high) speedCounts.high++;
    else speedCounts.extreme++;
  });

  const total = devices.length || 1;
  const chartData = [
    { range: labels.low, percent: parseFloat(((speedCounts.low / total) * 100).toFixed(2)), type: 'low' },
    { range: labels.mid, percent: parseFloat(((speedCounts.mid / total) * 100).toFixed(2)), type: 'mid' },
    { range: labels.high, percent: parseFloat(((speedCounts.high / total) * 100).toFixed(2)), type: 'high' },
    { range: labels.extreme, percent: parseFloat(((speedCounts.extreme / total) * 100).toFixed(2)), type: 'extreme' },
  ];

  const handleFetch = async (type) => {
    const token = localStorage.getItem("token");
    const formatForApi = (ts) => ts?.replace(/-/g, "/").replace("T", " ") || "";
    const startDate = storedTimes ? storedTimes.split("T")[0] : new Date().toISOString().split("T")[0];
    const endDate = endTimeStored ? endTimeStored.split("T")[0] : startDate;

    try {
      let response;
      if (type === "HALT") {
        response = await axios.post("https://v3.dhundhoo.com/reports/haltTripReport", {
          date: '', zone: "Asia/Kolkata", startTime: formatForApi(storedTimes), endTime: formatForApi(endTimeStored),
          things: vehicleId, interval: 10, thingIds: [], geofence: [], groups: [], orgName: "", geoFenceName: "", geoLat: 0, geoLong: 0,
        }, { headers: { "x-dhundhoo-session": token } });
        globalTripTitle = "Halt Trip Analysis";
      }
      else if (type === "IGNITION") {
        response = await axios.post(`https://v3.dhundhoo.com/reports/ignitionTripReport/${vehicleId}`,
          { startDate: startDate, endDate: endDate },
          { headers: { "x-dhundhoo-session": token } });
        globalTripTitle = "Ignition Trip Analysis";
      }
      else if (type === "GEOFENCE") {
        response = await axios.post("https://v3.dhundhoo.com/thing/geofenceTrip", {
          startTime: formatForApi(storedTimes), endTime: formatForApi(endTimeStored), zone: "Asia/Kolkata",
          things: vehicleId, thingIds: [vehicleId]
        }, { headers: { "x-dhundhoo-session": token } });
        globalTripTitle = "Geofence Trip Analysis";
      }

      globalTripData = response.data;
      onDrawerStateChange(true);
      setRefresh(prev => prev + 1);
    } catch (err) {
      console.error("Fetch Error:", err);
    }
  };

  const handleRowClick = (trip) => {
    const segment = {
      startTime: trip.tripStartTime || trip.startTime || trip.ignStartTimeDate || trip.ignStartTime,
      endTime: trip.tripEndTime || trip.endTime || trip.ignEndTimeDate || trip.ignEndTime,
      type: globalTripTitle.toLowerCase().includes("ignition") ? "IGNITION" : "HALT",
    };
    dispatch(setHighlightSegment(segment));
  };

  const handleLocationClick = (e, lat, lng) => {
    e.stopPropagation();
    if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  const handleClose = () => {
    dispatch(setHighlightSegment(null));
    onDrawerStateChange(false);
  };

  const getColor = (type) => {
    const colors = { low: "rgb(43, 191, 159)", mid: "rgb(251, 164, 0)", high: "rgb(240, 120, 70)", extreme: "#ff0000" };
    return colors[type];
  };

  if (isInsideSidebar) {
    return (
      <MDBox p={2} pt={0}>
        <Card sx={{ borderRadius: 2, boxShadow: 2, p: 1, bgcolor: "#fff", mt: 2 }}>
          <CardContent sx={{ p: "16px !important" }}>
            <MDBox display="flex" gap={1} mb={2} flexWrap="wrap">
              <MDButton variant="gradient" color="info" size="small" sx={{ flex: 1 }} onClick={() => handleFetch("HALT")}>Halt</MDButton>
              <MDButton variant="gradient" color="dark" size="small" sx={{ flex: 1 }} onClick={() => handleFetch("IGNITION")}>Ignition</MDButton>
              <MDButton variant="gradient" color="success" size="small" sx={{ flex: 1 }} onClick={() => handleFetch("GEOFENCE")}>Geofence</MDButton>
            </MDBox>

            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", textTransform: 'uppercase', fontSize: '10px', color: '#7b809a' }}>
                  Custom Speed Ranges (Knots)
                </Typography>
                <IconButton size="small" onClick={handleOpenSpeedModal} sx={{ p: 0.5 }}>
                  <TuneIcon sx={{ fontSize: "16px", color: "#7b809a" }} />
                </IconButton>
              </Box>
              <MDButton variant="outlined" color="dark" size="small" fullWidth onClick={handleOpenSpeedModal} sx={{ fontSize: "11px", py: 0.7 }}>
                {`< ${thresholds.low}  •  ${thresholds.low}-${thresholds.med}  •  ${thresholds.med}-${thresholds.high}  •  > ${thresholds.high}`}
              </MDButton>
            </Box>

            <Dialog open={speedModalOpen} onClose={handleCancelSpeedModal} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontSize: "16px", fontWeight: 600 }}>Set Custom Speed Ranges (Knots)</DialogTitle>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                  {['low', 'med', 'high'].map((key) => (
                    <TextField
                      key={key}
                      label={`${key.charAt(0).toUpperCase() + key.slice(1)} Threshold`}
                      type="number"
                      size="small"
                      fullWidth
                      value={draftThresholds[key]}
                      onChange={(e) => handleDraftChange(key, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  ))}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCancelSpeedModal} color="secondary">Cancel</Button>
                <Button onClick={handleSaveSpeedModal} variant="contained" sx={{color:'#fff'}}>Save</Button>
              </DialogActions>
            </Dialog>

            <Divider sx={{ my: 1.5, opacity: 0.5 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 1 }}>📈 Speed Distribution</Typography>
            <Box sx={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barSize={55}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 9, fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ fontSize: "0.7rem", borderRadius: '8px' }} formatter={(val) => [`${val}%`, 'Percentage']} />
                  <Bar dataKey="percent" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getColor(entry.type)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ flex: 1, bgcolor: "white", borderRadius: "24px", boxShadow: "0 -4px 12px rgba(0,0,0,0.05)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 24px", background: "#f8f9fa", borderBottom: "1px solid #ddd" }}>
        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#344767" }}>{globalTripTitle}</span>
        <IconButton size="small" onClick={handleClose}><CloseIcon sx={{ fontSize: "18px" }} /></IconButton>
      </div>

      <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
     <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
          <tr style={{ background: "#f8f9fa" }}>
            <th style={{ width: "10%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Trip ID</th>
            <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>START TIME</th>
            <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
              {globalTripTitle.includes("Geofence") ? "START FENCE" : "START LOCATION"}
            </th>
            <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>END TIME</th>
            <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
              {globalTripTitle.includes("Geofence") ? "END FENCE" : "END LOCATION"}
            </th>
          </tr>
        </thead>
        <tbody>
          {globalTripData && Object.entries(globalTripData).map(([id, trip]) => {
            const isGeofence = globalTripTitle.includes("Geofence");
            const startTime = trip.tripStartTime || trip.startTime || trip.ignStartTime;
            const endTime = trip.tripEndTime || trip.endTime || trip.ignEndTime;

            return (
              <tr key={id} 
                  onClick={() => handleRowClick(trip)} 
                  onMouseEnter={() => setHoveredRow(id)} 
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{ borderBottom: "1px solid #eee", cursor: "pointer", backgroundColor: hoveredRow === id ? "#f0f7ff" : "transparent", transition: "background-color 0.2s ease" }}>
                <td style={{ padding: "12px", fontSize: "11px" }}>{trip.srno || id}</td>
                <td style={{ padding: "12px", fontSize: "11px" }}>{startTime}</td>
                <td style={{ padding: "12px" }}>
                  {isGeofence ? (
                    <MDTypography variant="caption" fontWeight="medium">{trip.startFenceName}</MDTypography>
                  ) : (
                    <MDBox display="flex" alignItems="center" gap={0.5} sx={{ "&:hover": { opacity: 0.7 } }}>
                      <LocationOnIcon sx={{ fontSize: "14px", color: "#1A73E8" }} />
                      <MDTypography variant="caption" color="info" fontWeight="medium" sx={{ fontSize: "11px" }}>View Start</MDTypography>
                    </MDBox>
                  )}
                </td>
                <td style={{ padding: "12px", fontSize: "11px" }}>{endTime}</td>
                <td style={{ padding: "12px" }}>
                  {isGeofence ? (
                    <MDTypography variant="caption" fontWeight="medium">{trip.endFenceName}</MDTypography>
                  ) : (
                    <MDBox display="flex" alignItems="center" gap={0.5} sx={{ "&:hover": { opacity: 0.7 } }}>
                      <LocationOnIcon sx={{ fontSize: "14px", color: "#F44335" }} />
                      <MDTypography variant="caption" sx={{ fontSize: "11px", color: "#F44335", fontWeight: "medium" }}>View End</MDTypography>
                    </MDBox>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </MDBox>
  );
};

export default SpeedAnalysis;